import { useRef, useState, useEffect, useCallback } from 'react';

interface UseTabsScrollResult {
  tabsRef: React.RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
  recalculate: () => void;
}

const SCROLL_STEP = 180;

export const useTabsScroll = (): UseTabsScrollResult => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const recalculate = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;

    recalculate();

    el.addEventListener('scroll', recalculate);
    const resizeObserver = new ResizeObserver(recalculate);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', recalculate);
      resizeObserver.disconnect();
    };
  }, [recalculate]);

  const scrollLeft = useCallback(() => {
    tabsRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    tabsRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
  }, []);

  return {
    tabsRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    recalculate,
  };
};
